// One-shot MediaPipe FaceLandmarker wrapper for the photo-based VTO pipeline.
//
// Unlike the realtime paradigm (which keeps a FaceLandmarker instance alive
// and calls detectForVideo at 30fps), we instantiate on demand, run a single
// detect() against an ImageBitmap, and keep the instance around only so
// multiple snapshots in the same session don't re-download the WASM runtime.
//
// All assets load from our own origin — no CDN fetch — to honour the
// local-first / privacy guarantees in virtual_try_on.md §7.
//
// Session A (v0.3.0): also requests `facialTransformationMatrixes` so each
// detection carries the head pose. Overlays consume roll (for rotating masks
// into face-frame) and yaw (for foreshortening ellipse gradients); downstream
// callers can clamp on extreme yaw/pitch to surface a "ruota il viso" hint.

import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult
} from '@mediapipe/tasks-vision';

const BASE = import.meta.env.BASE_URL;
const WASM_BASE = `${BASE}assets/mediapipe-wasm`;
const MODEL_URL = `${BASE}assets/models/face_landmarker.task`;

let instancePromise: Promise<FaceLandmarker> | null = null;

// Detection/presence confidence gates. Previously left implicit (MediaPipe
// default 0.5); now explicit and tuned slightly upward so low-confidence
// phantom detections (hands, patterned backgrounds, partial faces) don't pass
// the 478-landmark gate and feed garbage geometry into the makeup pipeline.
// Kept conservative (0.6) to avoid rejecting valid-but-imperfect selfies —
// pose/blur/lighting are handled downstream in faceQuality.ts.
const MIN_FACE_DETECTION_CONFIDENCE = 0.6;
const MIN_FACE_PRESENCE_CONFIDENCE = 0.6;

async function create(): Promise<FaceLandmarker> {
  const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
  return FaceLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: MODEL_URL },
    runningMode: 'IMAGE',
    numFaces: 1,
    minFaceDetectionConfidence: MIN_FACE_DETECTION_CONFIDENCE,
    minFacePresenceConfidence: MIN_FACE_PRESENCE_CONFIDENCE,
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: true
  });
}

export function getFaceLandmarker(): Promise<FaceLandmarker> {
  if (!instancePromise) instancePromise = create();
  return instancePromise;
}

export interface FacePose {
  // Raw 4×4 transformation matrix from MediaPipe (canonical face → camera
  // space), column-major storage: element (row i, col j) = matrix[j*4 + i].
  // null when MediaPipe didn't return a matrix (older runtime, or detection
  // failed but landmarks passed the length gate below).
  matrix: number[] | null;
  // Euler angles in radians, extracted with a canvas-friendly convention:
  //   roll  = CW rotation in image space; apply directly as ctx.rotate(roll)
  //           to bring face-up into canvas-up.
  //   yaw   = head turn left/right (sign = MediaPipe convention, but |yaw|
  //           is what overlays use for cos-foreshortening so sign is moot).
  //   pitch = head nod up/down (same caveat as yaw on sign).
  // Derivation: face-up in camera space = column 1 of matrix. Image Y is
  // flipped vs camera Y, so face-up in image = (m01, -m11). The CW angle
  // from image-up (0,-1) to (m01, -m11) is atan2(m01, m11). See README
  // handoff notes for the full derivation.
  roll: number;
  yaw: number;
  pitch: number;
}

const NEUTRAL_POSE: FacePose = {
  matrix: null,
  roll: 0,
  yaw: 0,
  pitch: 0
};

// Extract pose from MediaPipe's column-major 4×4. Returns NEUTRAL_POSE on
// any shape or numerical surprise — overlays treat that as "render as if
// the face were dead-centre", which matches the pre-pose behaviour exactly.
function extractPose(raw: { data: number[] } | undefined): FacePose {
  if (!raw || !raw.data || raw.data.length < 16) return NEUTRAL_POSE;
  const m = raw.data;
  // Column-major indexing helpers: r<i><j> = row i, col j = m[j*4 + i].
  const r01 = m[4]!;
  const r11 = m[5]!;
  const r12 = m[9]!;
  const r02 = m[8]!;
  const r22 = m[10]!;
  const r20 = m[2]!;
  const r00 = m[0]!;
  // Canvas-friendly roll: angle from image-up to face-up, CW, radians.
  const roll = Math.atan2(r01, r11);
  // YXZ-style pitch extraction (robust under normal face poses).
  const sinPitch = Math.max(-1, Math.min(1, -r12));
  const pitch = Math.asin(sinPitch);
  // Yaw: head turn around the vertical axis. Stable away from ±90° pitch.
  let yaw: number;
  if (Math.abs(r12) < 0.9999) {
    yaw = Math.atan2(r02, r22);
  } else {
    // Gimbal-lock fallback (nose pointing straight up/down — unusual for
    // a face-portrait photo but cheap to guard against).
    yaw = Math.atan2(-r20, r00);
  }
  return { matrix: m, roll, yaw, pitch };
}

export interface LipDetection {
  // Normalised [0..1] landmark coordinates (x,y,z) in the original image space.
  landmarks: { x: number; y: number; z: number }[];
  imageWidth: number;
  imageHeight: number;
  // Head pose, used by overlays to align their masks with the face frame
  // instead of the image frame. NEUTRAL_POSE if MediaPipe omitted the
  // matrix — in that case overlays render exactly as they did pre-v0.3.0.
  pose: FacePose;
}

export async function detectLips(
  source: ImageBitmap | HTMLCanvasElement | HTMLImageElement
): Promise<LipDetection | null> {
  const landmarker = await getFaceLandmarker();
  const result: FaceLandmarkerResult = landmarker.detect(source);
  const face = result.faceLandmarks?.[0];
  if (!face || face.length < 478) return null;

  // HTMLImageElement.width/height riflette il layout (può essere 0 fuori
  // dal DOM, o ridotto via CSS). naturalWidth/Height è l'intrinseca.
  // ImageBitmap e HTMLCanvasElement hanno solo width/height intrinseci.
  const width = source instanceof HTMLImageElement
    ? source.naturalWidth || source.width
    : 'width' in source ? source.width : 0;
  const height = source instanceof HTMLImageElement
    ? source.naturalHeight || source.height
    : 'height' in source ? source.height : 0;

  const rawMatrix = result.facialTransformationMatrixes?.[0];
  const pose = extractPose(rawMatrix);

  return {
    landmarks: face.map((p) => ({ x: p.x, y: p.y, z: p.z })),
    imageWidth: width,
    imageHeight: height,
    pose
  };
}

export function disposeFaceLandmarker(): void {
  if (!instancePromise) return;
  instancePromise
    .then((l) => l.close())
    .catch(() => undefined)
    .finally(() => {
      instancePromise = null;
    });
}
